import React from 'react';
import { CardFinish } from 'utils/finishes';
import { FinishTag } from 'components/FinishTag';
import './FoilCardImage.css';

interface FoilCardImageProps {
  src: string;
  alt: string;
  /**
   * The finish being rendered. Gates the treatment (Decision 12 as revised,
   * 2026-09-08): 'etched' paints the metallic sheen plus the ETCHED badge,
   * 'foil' the existing rainbow overlay, 'nonfoil' nothing. Always the ROW's
   * finish (or, on a printings list, the finish being rendered), never the
   * image source: there is no way to tell which scan a printing carries.
   */
  finish?: CardFinish;
  /** Scryfall promo_types of the printing: the tag shows the treatment swatch and suffix (addendum 2) */
  promoTypes?: unknown;
  className?: string;
  style?: React.CSSProperties;
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  loading?: 'lazy' | 'eager';
  borderRadius?: string;
}

/**
 * FoilCardImage - A reusable card image component with a finish-gated overlay
 *
 * Renders a card image with an optional sheen. Both treatments are pure CSS
 * (gradients and inline-SVG data URIs, no network assets) defined in
 * FoilCardImage.css. Because the overlays use `inset: 0` + `border-radius:
 * inherit` and percentage-based gradient geometry, they cover the exact card
 * bounds and keep a consistent look at any card size (S/M/L tiles, the large
 * Card Details view, mobile, the 140px fanned printings grid).
 *
 * The foil treatment is tunable via CSS custom properties (defaults live in the CSS):
 *   --foil-intensity   overlay opacity (0–1)
 *   --foil-angle       direction of the iridescent streaks
 *   --foil-position-x  horizontal center of the glare highlight
 *   --foil-position-y  vertical center of the glare highlight
 * A future interactive ticket can drive these from JS for dynamic light effects.
 */
export const FoilCardImage: React.FC<FoilCardImageProps> = ({
  src,
  alt,
  finish = 'nonfoil',
  promoTypes,
  className = '',
  style = {},
  onError,
  loading = 'lazy',
  borderRadius = '8px',
}) => {
  const resolvedFinish: CardFinish = finish;
  const isEtched = resolvedFinish === 'etched';

  return (
    <div
      className={`foil-card-image-wrapper ${className}`}
      data-finish={resolvedFinish}
      style={{
        ...style,
        borderRadius,
      }}
    >
      <img
        src={src}
        alt={alt}
        className={`foil-card-image${isEtched ? ' etched' : ''}`}
        style={{ borderRadius }}
        onError={onError}
        loading={loading}
      />
      {resolvedFinish === 'foil' && <div className="foil-card-overlay" aria-hidden="true" />}
      {isEtched && (
        /* Nine-layer stack per the approved Design Pass spec; each span is one layer */
        <div className="etched-card-overlay" aria-hidden="true">
          <span className="etched-layer etched-layer-shade" />
          <span className="etched-layer etched-layer-grain-bright" />
          <span className="etched-layer etched-layer-grain-dark" />
          <span className="etched-layer etched-layer-hex-light" />
          <span className="etched-layer etched-layer-hex-dark" />
          <span className="etched-layer etched-layer-sweep" />
          <span className="etched-layer etched-layer-soft" />
          <span className="etched-layer etched-layer-vignette" />
        </div>
      )}
      {/* Every premium finish carries the shared tag, bottom-left: the sheen
          alone is not legible at grid scale (QA round 1, 3.3). Non-foil is
          the unmarked default. */}
      {resolvedFinish !== 'nonfoil' && (
        <FinishTag finish={resolvedFinish} promoTypes={promoTypes} className="foil-card-image-tag" />
      )}
    </div>
  );
};

export default FoilCardImage;
